import React, { useState, useEffect, useMemo } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { streakDoc, dailyAnswersColl } from '../db';
import { UserProfile, Screen } from '../App';

interface Props { user: UserProfile; navigate: (s: Screen) => void; backTo?: Screen; }

interface StreakState {
  count: number;
  lastUpdatedDate: string;
  longestStreak: number;
  broken: boolean;
}

interface DayStatus {
  myAnswered: boolean;
  partnerAnswered: boolean;
}

function getTodayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getLast30Days(): string[] {
  return Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (29 - i));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
}

export default function StreakScreen({ user, navigate, backTo = 'us' }: Props) {
  const [streak, setStreak] = useState<StreakState | null>(null);
  const [dayStatuses, setDayStatuses] = useState<Map<string, DayStatus>>(new Map());
  const [displayCount, setDisplayCount] = useState(0);
  const today = getTodayKey();
  const last30 = useMemo(() => getLast30Days(), []);
  const partnerName = user.displayName === 'Serhat' ? 'Ayça' : 'Serhat';

  useEffect(() => {
    return onSnapshot(streakDoc(user.coupleId), snap => {
      if (snap.exists()) setStreak(snap.data() as StreakState);
    });
  }, [user.coupleId]);

  useEffect(() => {
    return onSnapshot(dailyAnswersColl(user.coupleId), snap => {
      const map = new Map<string, DayStatus>();
      snap.docs.forEach(d => {
        const uids = Object.keys(d.data());
        map.set(d.id, {
          myAnswered: uids.includes(user.uid),
          partnerAnswered: uids.some(u => u !== user.uid),
        });
      });
      setDayStatuses(map);
    });
  }, [user.coupleId, user.uid]);

  useEffect(() => {
    if (!streak) return;
    const target = streak.count;
    if (target === 0) { setDisplayCount(0); return; }
    let current = 0;
    const steps = Math.min(target, 40);
    const intervalMs = 1200 / steps;
    const step = Math.ceil(target / steps);
    const timer = setInterval(() => {
      current = Math.min(current + step, target);
      setDisplayCount(current);
      if (current >= target) clearInterval(timer);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [streak?.count]); // eslint-disable-line

  const todayStatus = dayStatuses.get(today);
  const count = streak?.count ?? 0;
  const currentInCycle = count % 7;
  const nextMilestone = count + (currentInCycle === 0 ? 7 : 7 - currentInCycle);
  const daysLeft = nextMilestone - count;
  const cycleProgress = (currentInCycle / 7) * 100;
  const fireSize = count >= 30 ? 88 : count >= 8 ? 68 : 52;

  const getBanner = () => {
    if (!todayStatus) return { text: 'Bugün henüz cevap yok', emoji: '○', color: 'rgba(255,255,255,0.5)', bg: 'rgba(255,255,255,0.045)', border: 'rgba(255,255,255,0.08)' };
    const both = todayStatus.myAnswered && todayStatus.partnerAnswered;
    if (both) return { text: 'Bugün ritme eklendi', emoji: '●', color: '#C8FFD2', bg: 'rgba(125,255,143,0.1)', border: 'rgba(125,255,143,0.18)' };
    const waitingName = todayStatus.myAnswered ? partnerName : user.displayName;
    return { text: `${waitingName} yazınca bugün de eklenir`, emoji: '◐', color: 'rgba(255,255,255,0.68)', bg: 'rgba(191,90,242,0.09)', border: 'rgba(191,90,242,0.16)' };
  };

  const banner = getBanner();

  return (
    <div style={{ minHeight: '100vh', paddingBottom: 80 }}>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(backTo)}>←</button>
        <h1 style={styles.title}>Ritmimiz</h1>
      </div>

      <div style={styles.main}>
        {/* Hero — fire + count */}
        <div style={styles.heroSection}>
          <motion.span
            animate={count > 0 ? {
              scale: [1, 1.1, 0.95, 1.08, 1],
              filter: ['brightness(1)', 'brightness(1.25)', 'brightness(0.9)', 'brightness(1.2)', 'brightness(1)'],
            } : { scale: [1, 1.03, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
            style={{ fontSize: fireSize, lineHeight: 1, display: 'block' }}
          >
            {streak?.broken && count === 0 ? '○' : '↟'}
          </motion.span>

          <motion.div
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 180, damping: 14, delay: 0.15 }}
            style={styles.countDisplay}
          >
            {displayCount}
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            style={styles.countLabel}
          >
            gün üst üste
          </motion.p>
        </div>

        {/* Today status banner */}
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          style={{ ...styles.statusBanner, background: banner.bg, border: `1px solid ${banner.border}` }}
        >
          <span style={{ fontSize: 20 }}>{banner.emoji}</span>
          <p style={{ color: banner.color, fontSize: 14, fontWeight: 600 }}>{banner.text}</p>
        </motion.div>

        {/* Longest streak record */}
        {streak && streak.longestStreak > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            style={styles.recordCard}
          >
            <span style={{ fontSize: 28 }}>🏆</span>
            <div>
              <p style={styles.recordLabel}>En uzun ritim</p>
              <p style={styles.recordVal}>{streak.longestStreak} gün</p>
            </div>
            {streak.longestStreak === count && count > 0 && (
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                style={{ marginLeft: 'auto', fontSize: 18 }}
              >
                ✨
              </motion.span>
            )}
          </motion.div>
        )}

        {/* Milestone progress */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          style={styles.milestoneCard}
        >
          <div style={styles.milestoneHeader}>
            <span style={{ fontSize: 20 }}>🎯</span>
            <p style={styles.milestoneText}>
              {daysLeft === 0
                ? `${nextMilestone} gün oldu.`
                : `${nextMilestone} güne ${daysLeft} gün kaldı.`}
            </p>
          </div>
          <div style={styles.progressTrack}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${cycleProgress}%` }}
              transition={{ duration: 1.1, delay: 0.9, ease: 'easeOut' }}
              style={styles.progressFill}
            />
          </div>
          <div style={styles.progressLabels}>
            <span>{currentInCycle} gün</span>
            <span>{daysLeft} gün kaldı</span>
          </div>
        </motion.div>

        {/* 30-day calendar */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75 }}
          style={styles.calendarCard}
        >
          <p style={styles.calendarTitle}>Son 30 Gün</p>
          <div style={styles.calendarGrid}>
            {last30.map((date, i) => {
              const st = dayStatuses.get(date);
              const isToday = date === today;
              const both = st?.myAnswered && st?.partnerAnswered;
              const one = st && (st.myAnswered || st.partnerAnswered) && !both;

              return (
                <motion.div
                  key={date}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.8 + i * 0.018, type: 'spring', stiffness: 320, damping: 18 }}
                  style={{
                    ...styles.calDay,
                    background: both
                      ? 'linear-gradient(135deg, #E91E8C, #9C27B0)'
                      : one
                        ? 'linear-gradient(135deg, rgba(233,30,140,0.55), rgba(156,39,176,0.25))'
                        : 'rgba(255,255,255,0.07)',
                    outline: isToday ? '2px solid #FFD700' : 'none',
                    outlineOffset: 2,
                  }}
                >
                  {isToday && (
                    <motion.div
                      animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }}
                      transition={{ duration: 1.4, repeat: Infinity }}
                      style={styles.todayRing}
                    />
                  )}
                </motion.div>
              );
            })}
          </div>
          <div style={styles.calLegend}>
            <div style={styles.legendItem}>
              <div style={{ ...styles.legendDot, background: 'linear-gradient(135deg, #E91E8C, #9C27B0)' }} />
              <span>İkisi de</span>
            </div>
            <div style={styles.legendItem}>
              <div style={{ ...styles.legendDot, background: 'linear-gradient(135deg, rgba(233,30,140,0.55), rgba(156,39,176,0.3))' }} />
              <span>Biri</span>
            </div>
            <div style={styles.legendItem}>
              <div style={{ ...styles.legendDot, background: 'rgba(255,255,255,0.07)' }} />
              <span>Hiçbiri</span>
            </div>
          </div>
        </motion.div>

        <motion.button
          whileTap={{ scale: 0.96 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          onClick={() => navigate('question')}
          style={styles.questionBtn}
        >
          Günün sorusuna dön
        </motion.button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontFamily: "'Inter',sans-serif", fontSize: 22, fontWeight: 850, letterSpacing: '-0.03em' },
  main: { padding: '8px 16px 0', display: 'flex', flexDirection: 'column', gap: 14 },
  heroSection: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '20px 0 8px' },
  countDisplay: {
    fontFamily: "'Inter',sans-serif", fontSize: 96, fontWeight: 900, lineHeight: 1,
    background: 'linear-gradient(135deg, #FFFFFF, #BF5AF2)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  countLabel: { fontSize: 16, color: 'rgba(255,255,255,0.55)', fontWeight: 650 },
  statusBanner: {
    padding: '12px 18px', borderRadius: 16,
    display: 'flex', alignItems: 'center', gap: 10,
  },
  recordCard: {
    background: 'rgba(255,215,0,0.07)', border: '1px solid rgba(255,215,0,0.2)',
    borderRadius: 20, padding: '14px 18px',
    display: 'flex', alignItems: 'center', gap: 14,
  },
  recordLabel: { fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 2, textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  recordVal: { fontSize: 22, fontWeight: 850, color: 'rgba(255,255,255,0.88)', fontFamily: "'Inter',sans-serif" },
  milestoneCard: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 20, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12,
  },
  milestoneHeader: { display: 'flex', gap: 10, alignItems: 'center' },
  milestoneText: { fontSize: 14, color: 'rgba(255,255,255,0.8)', flex: 1 },
  progressTrack: { height: 8, background: 'rgba(255,255,255,0.09)', borderRadius: 4, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 4, background: 'linear-gradient(90deg, #E91E8C, #C77DFF)' },
  progressLabels: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(255,255,255,0.4)' },
  calendarCard: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 20, padding: 16,
  },
  calendarTitle: {
    fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' as const,
    letterSpacing: 1, marginBottom: 14, textAlign: 'center' as const,
  },
  calendarGrid: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 7 },
  calDay: { aspectRatio: '1' as any, borderRadius: '50%', position: 'relative' as const, overflow: 'visible' as const },
  todayRing: {
    position: 'absolute' as const, inset: -4,
    borderRadius: '50%', border: '2px solid #FFD700',
    pointerEvents: 'none' as any,
  },
  calLegend: { display: 'flex', justifyContent: 'center', gap: 18, marginTop: 12 },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.45)' },
  legendDot: { width: 12, height: 12, borderRadius: '50%', flexShrink: 0 },
  questionBtn: {
    padding: '14px 24px', borderRadius: 20, marginBottom: 8,
    background: 'linear-gradient(135deg, rgba(233,30,140,0.18), rgba(156,39,176,0.18))',
    border: '1px solid rgba(233,30,140,0.3)',
    color: 'white', fontSize: 15, fontWeight: 600, cursor: 'pointer', width: '100%',
  },
};
